// src/modules/finance/table/AllCashboxTransactionsTable.tsx
import React from "react";
import { useTranslation } from "react-i18next";
import Table from "components/Common/dataTable";
import { CashTransaction } from "types/cashTransactionTypes";
import { formatMoney } from "helpers/numberHelper";
import { formatToLocaleDate } from "utils/dateUtils";
import { getLabelPaymentMethod } from "utils/stringUtils";
import Badge from "components/Common/ui/Badge";
import CustomButton from "components/Common/ui/CustomButton";
import UserProfileHeader from "components/Common/ui/UserProfileHeader";

type CashTransactionWithRef = CashTransaction & {
    reference?: any;
};

type Props = {
    data: CashTransactionWithRef[];
    loading?: boolean;
    handleViewDetails: (transaction: CashTransactionWithRef) => void;
    dataPaginate: any;
    onChange: (pg: any) => void;
};

const AllCashboxTransactionsTable: React.FC<Props> = ({ data, loading, handleViewDetails, dataPaginate,
    onChange, }) => {
    const { t } = useTranslation();

    const columns = [
        {
            title: t("user"),
            key: "user",
            render: (v: string, r: any) => <UserProfileHeader firstName={r.creator?.firstName || ""} lastName={r.creator?.lastName || ""} photo={r.creator?.photo || ""} subtitle={r.creator?.email || ""} />,
        },
        {
            title: t("cashboxTransactions.fields.date"),
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value: string) => formatToLocaleDate(value, "L LT"),
            width: "15%",
        },
        {
            title: t("cashboxTransactions.fields.direction"),
            dataIndex: "type",
            key: "type",
            render: (value: "in" | "out") => {
                const text = value === "in"
                    ? t("cashboxTransactions.type.in")
                    : t("cashboxTransactions.type.out");
                const color = value === "in"
                    ? "success"
                    : "danger";

                return <Badge text={text} variant={color} />
            },

            width: "10%",
        },
        {
            title: t("cashboxTransactions.fields.amount"),
            dataIndex: "amount",
            key: "amount",
            render: (value: string | number) => `${formatMoney(Number(value))}`,
            width: "15%",
        },
        {
            title: t("cashboxTransactions.fields.operation"),
            dataIndex: "reference",
            key: "operation",
            width: "35%",
            render: (_: any, record: CashTransactionWithRef) => {
                const { referenceType, reference } = record;

                if (!referenceType || !reference) {
                    return "-";
                }

                if (referenceType === "FeePayment") {
                    const studentName =
                        reference.fee?.student?.user
                            ? `${reference.fee.student.user.firstName || ""} ${reference.fee.student.user.lastName || ""
                                }`.trim()
                            : null;
                    const feeTypeName = reference.fee?.feeType?.name || null;

                    return (
                        <span>
                            {t("cashboxTransactions.operation.fee_payment")}
                            {feeTypeName ? ` - ${feeTypeName}` : ""}
                            {studentName ? ` - ${studentName}` : ""}
                        </span>
                    );
                }

                if (referenceType === "Expense") {
                    const categoryName = reference.category?.name || null;
                    const supplierName = reference.supplier?.name || null;

                    return (
                        <span>
                            {t("cashboxTransactions.operation.expense")}
                            {categoryName ? ` - ${categoryName}` : ""}
                            {supplierName ? ` - ${supplierName}` : ""}
                        </span>
                    );
                }

                return "-";
            },
        },
        {
            title: t("cashboxTransactions.fields.payment_type"),
            dataIndex: "reference",
            key: "paymentType",
            width: "15%",
            render: (_: any, record: CashTransactionWithRef) => {
                const { referenceType, reference } = record;

                if (referenceType === "FeePayment") {
                    const methodName = reference?.paymentMethod?.name;
                    return getLabelPaymentMethod(methodName);
                }

                if (referenceType === "Expense") {
                    return getLabelPaymentMethod("Espèces");
                }

                return "-";
            },
        },
        {
            title: t("actions"),
            key: "actions",
            width: "15%",
            render: (_: any, record: CashTransactionWithRef) => (
                <CustomButton
                    onClick={() => handleViewDetails(record)}
                >
                    <i className="ti ti-eye me-2"></i>

                    {t('details')}
                </CustomButton>
            ),
        },
    ];

    return (
        <div className="table-responsive">
            <Table
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={true}
                dataPaginate={dataPaginate}
                onChange={onChange}
            />
        </div>
    );
};

export default AllCashboxTransactionsTable;
